import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component to catch rendering errors and provide fallback UI.
 */
// Fix: Explicitly extend Component from 'react' to ensure setState and props are inherited correctly
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // Fix: Explicitly type the state property for TypeScript inheritance
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
  }

  // Fix: Use correct ErrorBoundaryState return type for static lifecycle method
  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  // Fix: componentDidCatch now correctly recognizes setState from the parent class
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error caught by ErrorBoundary:", error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  // Fix: handleDismiss now correctly recognizes setState from the parent class
  private handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render(): ReactNode {
    // Fix: Access state from this.state as typed by the parent Component class
    const { hasError, error } = this.state;

    if (hasError) {
      // Safely extract error message
      let errorMsg = "An unexpected error occurred causing the application to crash.";
      let stackTrace = "";

      if (error) {
        errorMsg = typeof error.message === 'string' ? error.message : JSON.stringify(error.message);
        stackTrace = error.stack || "";
      }

      // Filter out [object Object] generic messages
      if (errorMsg === '[object Object]') {
          errorMsg = "An unknown error occurred.";
      }

      // Check for specific backend setup issues to provide better guidance
      const isSetupIssue = errorMsg.toLowerCase().includes('relation') || 
                           errorMsg.toLowerCase().includes('42p01') || 
                           errorMsg.toLowerCase().includes('schema cache');

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8 font-sans">
          <div className="max-w-xl w-full space-y-8 bg-white p-10 rounded-xl shadow-2xl border border-gray-200">
            <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                <svg className="h-10 w-10 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
                  {isSetupIssue ? 'System Setup Required' : 'Application Error'}
                </h2>
                <p className="text-sm text-gray-500 mb-6">
                  {isSetupIssue 
                    ? "The database tables required for this application have not been created yet." 
                    : "Something went wrong while rendering the application."}
                </p>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 text-left overflow-auto max-h-40">
                <p className="text-xs font-mono text-red-700 whitespace-pre-wrap">{errorMsg}</p>
                {stackTrace && <p className="text-xs font-mono text-red-400 mt-2 whitespace-pre-wrap truncate">{stackTrace.split('\n')[0]}</p>}
            </div>

            <div className="flex flex-col space-y-3">
               <Button onClick={this.handleReload} className="w-full justify-center">
                  Reload Application
               </Button>
               {isSetupIssue && (
                   <p className="text-xs text-center text-gray-400 mt-2">
                       If reloading does not fix this, please ensure the Setup Assistant runs.
                   </p>
               )}
            </div>
          </div>
        </div>
      );
    }

    // Fix: Correctly access this.props.children inherited from Component
    return this.props.children;
  }
}
